import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { KDateFormat, KDateParse } from '@kiwid-app/kiwid-date-typescript';
import { Observable } from 'rxjs';

@Injectable()
export class ConvertHelper {
    static base64ToBlob(dataURI: string): { blob: Blob; mimeString: string } {
        let byteString;
        if (dataURI.split(',')[0].indexOf('base64') >= 0) {
            byteString = atob(dataURI.split(',')[1]);
        } else {
            byteString = unescape(dataURI.split(',')[1]);
        }
        const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
        const ia = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }

        return {
            blob: new Blob([ia], { type: mimeString }),
            mimeString,
        };
    }

    static convertBlobToUrl(domSanitizer: DomSanitizer, blob: Blob): string {
        const url = URL.createObjectURL(blob);
        return domSanitizer.sanitize(SecurityContext.URL, domSanitizer.bypassSecurityTrustUrl(url));
    }

    static convertFileToBase64(file: File): Observable<string | ArrayBuffer> {
        return new Observable((observer) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                observer.next(reader.result);
                observer.complete();
            };
            reader.onerror = (error) => {
                observer.error(error);
                observer.complete();
            };
        });
    }

    static convertBlobToBase64(blob: Blob): Observable<string> {
        return new Observable((observer) => {
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onload = () => {
                observer.next(reader.result.toString());
                observer.complete();
            };
            reader.onerror = (error) => {
                observer.error(error);
                observer.complete();
            };
        });
    }

    static convertBlobToFile(blob: Blob, mimeString: string): File {
        return new File([blob], `fileName.${mimeString.split('/')[1]}`, { type: mimeString });
    }

    static base64ToFile(dataURI: string, fileName: string = 'fileName'): File {
        const { blob, mimeString } = this.base64ToBlob(dataURI);
        return new File([blob], `${fileName}.${mimeString.split('/')[1]}`, { type: mimeString });
    }

    static dataURItoBlob(dataURI: string): { blob: Blob; mimeString: string } {
        let byteString;
        if (dataURI.split(',')[0].indexOf('base64') >= 0) {
            byteString = atob(dataURI.split(',')[1]);
        } else {
            byteString = unescape(dataURI.split(',')[1]);
        }
        const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
        const ia = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }

        return {
            blob: new Blob([ia], { type: mimeString }),
            mimeString,
        };
    }

    static parseStringToDate(dateString: string, format = 'yyyy-MM-dd'): Date {
        return KDateParse({ value: dateString, format });
    }

    static convertDateToStringApi(date: Date): string {
        return KDateFormat(date, 'yyyy-MM-dd');
    }

    static convertToMaskMoney(
        value: string | number,
        option: { decimalSeparator: string; prefix: string; thousandSeparator: string; suffix?: string },
        fractionDigits = 2,
    ): string {
        if (typeof value === 'string') {
            value = Number(value);
        }

        value = value.toFixed(fractionDigits);

        return ConvertHelper.convertToNumberOptions(value, option);
    }

    static convertToNumberOptions(
        value: string | number,
        option: { decimalSeparator: string; thousandSeparator: string; prefix?: string; suffix?: string },
    ): string {
        const { decimalSeparator, thousandSeparator } = option;

        if (typeof value === 'number') {
            value = String(value);
        }

        const values = value.split('.');
        const thousand = values[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);

        value = `${thousand}${values.length > 1 ? `${decimalSeparator}${values[1]}` : ''}`;

        return `${option?.prefix ?? ''}${value}${option?.suffix ?? ''}`;
    }

    static toFixedIfNecessary(value: string | number, maxFixed: number): number {
        if (typeof value === 'number') {
            value = String(value);
        }

        return +parseFloat(value).toFixed(maxFixed);
    }

    static convertObjectEmptyToNull<T>(object: T): T {
        (Array.isArray(object) ? object : [object]).forEach((objectItem) => {
            if (objectItem == null || typeof objectItem === 'number' || typeof objectItem === undefined) {
                return;
            }

            if (typeof objectItem === 'string') {
                if (objectItem === '') {
                    objectItem = null;
                }
                return;
            }

            Object.keys(objectItem).forEach((k) => {
                if (objectItem[k] == null) {
                    return;
                }

                if (Array.isArray(objectItem[k])) {
                    ConvertHelper.convertObjectEmptyToNull(objectItem[k]);
                    return;
                }

                if (typeof objectItem[k] === 'object') {
                    ConvertHelper.convertObjectEmptyToNull(objectItem[k]);
                    return;
                }

                objectItem[k] = objectItem[k] === '' || objectItem[k] === undefined ? null : objectItem[k];
            });
        });

        return object;
    }

    static toBoolean(value: string): boolean {
        return ['1', 1, 'true', true].includes(value);
    }
}
